import { useMemo } from 'react';
import { usePowerSyncQuery } from '../../adapters/powersync';
import {
  qCalendarSettings,
  mapCalendarSettingsToUI,
  type CalendarSettingsRow,
} from '@helios/data-access';
import { CALENDAR_SETTINGS_DEFAULTS } from '@helios/fixtures';

// ─── Types ───

export type WeekStartDay = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';
export type ColorAppointmentsBy = 'category' | 'provider' | 'status';

export interface CalendarSettings {
  filters: {
    category: boolean;
    status: boolean;
    provider: boolean;
    service: boolean;
    room: boolean;
    location: boolean;
  };
  weekStartsOn: WeekStartDay;
  workingHoursStartMin: number;
  workingHoursEndMin: number;
  slotIntervalMin: number;
  colorAppointmentsBy: ColorAppointmentsBy;
}

// ─── Hook ───

export function useAppointmentCalendarSettings(): CalendarSettings {
  const { data: rows = [] } = usePowerSyncQuery<CalendarSettingsRow>(qCalendarSettings());

  return useMemo(() => {
    if (rows.length > 0) {
      const ui = mapCalendarSettingsToUI(rows[0]);
      return {
        filters: {
          category: ui.categoryFilter,
          status: ui.statusFilter,
          provider: ui.providerFilter,
          service: ui.serviceFilter,
          room: ui.roomFilter,
          location: ui.locationFilter,
        },
        weekStartsOn: ui.weekStartsOn as WeekStartDay,
        workingHoursStartMin: ui.workingHoursStartMin,
        workingHoursEndMin: ui.workingHoursEndMin,
        slotIntervalMin: ui.slotIntervalMin,
        colorAppointmentsBy: ui.colorBy as ColorAppointmentsBy,
      };
    }

    // Fallback to fixture defaults
    const d = CALENDAR_SETTINGS_DEFAULTS;
    return {
      filters: {
        category: d.filter_category_enabled,
        status: d.filter_status_enabled,
        provider: d.filter_provider_enabled,
        service: d.filter_service_enabled,
        room: d.filter_room_enabled,
        location: d.filter_location_enabled,
      },
      weekStartsOn: d.week_starts_on as WeekStartDay,
      workingHoursStartMin: d.working_hours_start_min,
      workingHoursEndMin: d.working_hours_end_min,
      slotIntervalMin: d.slot_interval_min,
      colorAppointmentsBy: d.color_appointments_by as ColorAppointmentsBy,
    };
  }, [rows]);
}
